import { createSlice } from '@reduxjs/toolkit';
import { getAllClients, getClientSummary } from 'api/clients';

const initialState = {
    isLoading: false,
    list: [],
    total: 0,
    error: '',
    query: '',
    currentPage: 1,
    rowsPerPage: 10,
    showDisabled: false,
    tags: [],
    startDate: '',
    endDate: '',
    isTrial: false,
    sortField: 'displayName',
    sorting: 1,
    showOnlyInactive: false,
    showOnlyDeidentified: false,
    filterDisplayNameOnly: false,
    summary: {
        active: 0,
        inactive: 0,
        new: 0,
        isLoading: false,
        error: ''
    }
};

const slice = createSlice({
    name: 'deployments',
    initialState,
    reducers: {
        setDeployments(state, action) {
            state.list = action.payload?.data || [];
            state.total = action.payload?.total || 0;
            state.error = '';
            state.isLoading = false;
        },
        setLoading(state, action) {
            state.isLoading = action.payload;
        },
        setSummaryLoading(state, action) {
            state.summary.isLoading = action.payload;
            if (action.payload) state.summary.error = '';
        },
        setSummary(state, action) {
            state.summary = {
                active: action.payload?.active || 0,
                inactive: action.payload?.inactive || 0,
                new: action.payload?.new || 0,
                isLoading: false,
                error: ''
            };
        },
        setSummaryError(state, action) {
            state.summary.isLoading = false;
            state.summary.error = action.payload || 'Unable to fetch deployments summary.';
        },
        setError(state, action) {
            state.error = action.payload || 'Unable to fetch deployments.';
            state.list = [];
            state.total = 0;
            state.isLoading = false;
        },
        setSearchQuery(state, action) {
            state.currentPage = 1;
            state.query = action.payload || '';
        },
        setCurrentPage(state, action) {
            state.currentPage = action.payload;
        },
        setRowsPerPage(state, action) {
            state.currentPage = 1;
            state.rowsPerPage = action.payload;
        },
        setShowDisabled(state, action) {
            state.currentPage = 1;
            state.showDisabled = action.payload;
        },
        setSorting(state, action) {
            const field = action.payload;
            state.currentPage = 1;
            state.sorting = state.sortField === field && state.sorting === 1 ? -1 : 1;
            state.sortField = field;
        },
        setFilters(state, action) {
            state.currentPage = 1;
            Object.assign(state, action.payload);
        }
    }
});

export default slice.reducer;

export function fetchAllDeployments() {
    return async (dispatch, getState) => {
        try {
            dispatch(slice.actions.setLoading(true));
            const {
                currentPage,
                rowsPerPage,
                query,
                showDisabled,
                tags,
                startDate,
                endDate,
                isTrial,
                sortField,
                sorting,
                showOnlyInactive,
                showOnlyDeidentified,
                filterDisplayNameOnly
            } = getState().deployments;

            const result = await getAllClients({
                perPage: rowsPerPage,
                pageNo: currentPage,
                query,
                showDisabled,
                tags,
                startDate,
                endDate,
                isTrial,
                sortField,
                sorting,
                showOnlyInactive,
                showOnlyDeidentified,
                filterDisplayNameOnly
            });

            dispatch(slice.actions.setDeployments(result?.data));
        } catch (err) {
            console.error(err);
            dispatch(slice.actions.setError(err?.message));
        }
    };
}

export function fetchDeploymentsSummary() {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.setSummaryLoading(true));
            const result = await getClientSummary(false);
            dispatch(slice.actions.setSummary(result?.data));
        } catch (err) {
            console.error(err);
            dispatch(slice.actions.setSummaryError(err?.message));
        }
    };
}

export function searchClientsList(query) {
    return async (dispatch) => {
        dispatch(slice.actions.setSearchQuery(query));
    };
}

export function deploymentsCurrentPage(page) {
    return async (dispatch) => {
        dispatch(slice.actions.setCurrentPage(page));
    };
}

export function deploymentsRowsPerPage(rowsPerPage) {
    return async (dispatch) => {
        dispatch(slice.actions.setRowsPerPage(rowsPerPage));
    };
}

export function deploymentsSortBy(field) {
    return async (dispatch) => {
        dispatch(slice.actions.setSorting(field));
    };
}

export function setDeploymentsShowDisabled(showDisabled) {
    return async (dispatch) => {
        dispatch(slice.actions.setShowDisabled(showDisabled));
    };
}

export function setDeploymentFilters(filters) {
    return async (dispatch) => {
        dispatch(slice.actions.setFilters(filters));
    };
}
